import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Mail, Phone, Send, MapPin, Globe, MessageSquare,
  ArrowRight, Linkedin, ExternalLink, Clock, CheckCircle2
} from 'lucide-react';
import api from '../services/api';

import { FlutterFoxFooter } from '../components/FlutterFoxFooter';

export default function ContactUs() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Name, email, and message are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leads', {
        name: form.name, email: form.email, phone: form.phone,
        company: form.subject, notes: form.message
      });
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you shortly.');
    } catch {
      toast.error('Failed to send. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-950 text-white min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Nav */}
      <nav className="bg-slate-950/95 backdrop-blur-md border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Transsolution" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
            <span className="font-bold text-white text-sm tracking-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>TRANSSOLUTION</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Home</a>
            <a href="/about" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">About</a>
            <button onClick={() => navigate('/login')} className="text-sm text-slate-300 hover:text-white px-4 py-2">Login</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 md:py-28" data-testid="contact-hero">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Contact Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
            GET IN TOUCH
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Have questions about Transsolution ERP? Need a demo? Our team is here to help you streamline your logistics business.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-12" data-testid="contact-info">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="mailto:info@transsolution.online" className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-xl text-center group" data-testid="contact-email-card">
              <div className="w-14 h-14 rounded-xl bg-orange-600/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-600/20 transition-colors">
                <Mail size={24} className="text-orange-500" />
              </div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Email Us</p>
              <p className="text-base font-bold text-slate-200 group-hover:text-orange-400 transition-colors">info@transsolution.online</p>
            </a>
            <a href="tel:+918130095013" className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-xl text-center group" data-testid="contact-phone-card">
              <div className="w-14 h-14 rounded-xl bg-orange-600/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-600/20 transition-colors">
                <Phone size={24} className="text-orange-500" />
              </div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Call Us</p>
              <p className="text-base font-bold text-slate-200 group-hover:text-orange-400 transition-colors">+91 81300 95013</p>
            </a>
            <div className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-xl text-center group">
              <div className="w-14 h-14 rounded-xl bg-orange-600/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-600/20 transition-colors">
                <Clock size={24} className="text-orange-500" />
              </div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Business Hours</p>
              <p className="text-base font-bold text-slate-200">Mon - Sat, 10AM - 7PM IST</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Map */}
      <section className="py-16 bg-slate-900/30" data-testid="contact-form-section">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Send Us A Message</p>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                WE'D LOVE TO HEAR FROM YOU
              </h2>
              {submitted ? (
                <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-8 text-center" data-testid="contact-success">
                  <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-slate-400 mb-6">We'll get back to you within 24 hours.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="text-orange-500 hover:text-orange-400 text-sm font-semibold">Send Another Message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                      <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                        placeholder="Your name" data-testid="contact-name" required />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Email *</label>
                      <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                        placeholder="you@company.com" data-testid="contact-email" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Phone</label>
                      <input type="tel" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                        placeholder="+91 XXXXX XXXXX" data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Subject</label>
                      <input type="text" value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                        placeholder="How can we help?" data-testid="contact-subject" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Message *</label>
                    <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all min-h-[140px] resize-y"
                      placeholder="Tell us about your requirements..." data-testid="contact-message" required />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-bold py-3.5 rounded-lg tracking-wide transition-all flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="contact-submit">
                    {submitting ? 'SENDING...' : <><Send size={16} /> SEND MESSAGE</>}
                  </button>
                </form>
              )}
            </div>

            {/* Right Side Info */}
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-base font-bold mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>OUR PARENT COMPANY</h3>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center shrink-0">
                    <Globe size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-200 mb-1">Flutter Fox</h4>
                    <p className="text-sm text-slate-400 leading-relaxed mb-3">
                      Our parent company develops custom software solutions for every business need. From AI Receptionists to intelligent agents, Flutter Fox builds cutting-edge technology that transforms how businesses operate.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a href="https://flutterfox.in" target="_blank" rel="dofollow" className="text-xs text-orange-500 hover:text-orange-400 font-semibold flex items-center gap-1" data-testid="flutterfox-link">
                        <Globe size={12} /> flutterfox.in <ExternalLink size={10} />
                      </a>
                      <a href="https://www.linkedin.com/company/flutterfoxin" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-1" data-testid="flutterfox-linkedin">
                        <Linkedin size={12} /> LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-base font-bold mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>WHAT FLUTTER FOX BUILDS</h3>
                <ul className="space-y-3">
                  {[
                    'Custom Software Development',
                    'AI Agents & AI Receptionists',
                    'Enterprise ERP Solutions',
                    'Cloud-Native Applications',
                    'Mobile & Web Apps',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle2 size={14} className="text-orange-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-600/20 to-pink-600/10 border border-orange-500/20 rounded-xl p-6">
                <MessageSquare size={24} className="text-orange-500 mb-3" />
                <h3 className="font-bold text-slate-200 mb-2">Need a Custom Solution?</h3>
                <p className="text-sm text-slate-400 mb-4">Flutter Fox can build AI agents, custom ERPs, and automation tools tailored to your specific business needs.</p>
                <a href="https://flutterfox.in" target="_blank" rel="dofollow"
                  className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm font-bold transition-colors">
                  Visit Flutter Fox <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Logo" className="w-6 h-6 rounded object-contain bg-white p-0.5" />
            <span className="text-xs text-slate-500">&copy; 2026 Transsolution ERP. All Rights Reserved.</span>
          </div>
          <FlutterFoxFooter testId="contact-footer" />
        </div>
      </footer>
    </div>
  );
}
